import { MemberStatus, StoreRole } from '@app/shared';
import { ROUTES } from '@/shared/config/routes';
import type { MembershipSummary } from '../model/types';

type MembershipLike = Pick<MembershipSummary, 'storeId' | 'role' | 'status'>;

/** Landing route of a store selection, by the user's role in it. */
export function roleHomeRoute(role: string): string {
  if (role === StoreRole.OWNER) return ROUTES.admin.products;
  if (role === StoreRole.CLIENT) return ROUTES.shop.root;
  return ROUTES.noAccess;
}

/** Which store `currentStoreId` should point at: the persisted selection while it is still an
 *  APPROVED membership, otherwise the first APPROVED one, otherwise nothing. A pending/rejected/
 *  blocked membership is never selected — it grants no access. */
export function pickCurrentStoreId(
  memberships: readonly MembershipLike[],
  currentStoreId: string | null,
): string | null {
  const approved = memberships.filter((m) => m.status === MemberStatus.APPROVED);
  if (currentStoreId && approved.some((m) => m.storeId === currentStoreId)) return currentStoreId;
  return approved[0]?.storeId ?? null;
}

/** Selection after a membership refresh. `memberships === null` means the fetch *failed*: nothing is
 *  known, so the current selection stays as is (never confused with «no memberships», which
 *  legitimately clears it). */
export function nextCurrentStoreId(
  memberships: readonly MembershipLike[] | null,
  currentStoreId: string | null,
): string | null {
  return memberships === null ? currentStoreId : pickCurrentStoreId(memberships, currentStoreId);
}

export interface HomeDestination {
  /** Where `/` should send the user. */
  to: string;
  /** The store that must be selected before going there; `null` when no store is involved. */
  storeId: string | null;
}

/** Decision table of `RootRedirect`: no memberships → onboarding; an APPROVED one → its zone
 *  (OWNER → admin, CLIENT → shop); only non-approved ones → "my stores". */
export function resolveHomeDestination(
  memberships: readonly MembershipLike[],
  currentStoreId: string | null,
): HomeDestination {
  if (memberships.length === 0) return { to: ROUTES.onboarding, storeId: null };

  const storeId = pickCurrentStoreId(memberships, currentStoreId);
  const membership = memberships.find((m) => m.storeId === storeId);
  if (!storeId || !membership) return { to: ROUTES.connections, storeId: null };

  return { to: roleHomeRoute(membership.role), storeId };
}
